import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X, CheckCircle, Circle, ArrowRight, Rocket, UserPlus,
  Buildings, Broadcast, PaperPlaneTilt, Flask, ArrowSquareOut, Copy, Plus,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import IntegrationKitPreview from "@/components/IntegrationKitPreview";

/**
 * Iter-73.8 — Vendor Onboarding Wizard.
 *
 * One modal, 6 sequential steps, ~90 seconds start-to-finish:
 *   1. Create the affiliate org (contact + name)
 *   2. Mint the sandbox API key (auto — no user interaction)
 *   3. Invite the vendor's login user (optional; skippable)
 *   4. Pick or create the buyer/client this affiliate ships to
 *   5. Mint a routing campaign linking affiliate → client
 *   6. Send the integration kit email (optional) + fire a canary lead
 *
 * Every step is idempotent on the backend so re-entering the wizard
 * mid-flow after a network hiccup won't create duplicates. Every step
 * that succeeds locks its inputs and stamps a green ✓ so the operator
 * can see exactly where they are.
 *
 * Super_admin only (the underlying endpoints all enforce it).
 */
export default function VendorOnboardingWizard({ open, onClose, onFinished }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [affiliate, setAffiliate] = useState(null);       // created org
  const [inviteResult, setInviteResult] = useState(null); // {status, email}
  const [clients, setClients] = useState([]);
  const [pickedClientId, setPickedClientId] = useState("");
  const [campaign, setCampaign] = useState(null);
  const [kitSent, setKitSent] = useState(null);
  // Iter-73.9 — inline kit preview so admins can copy the kit into their
  // personal email client without leaving the wizard.
  const [kitPreview, setKitPreview] = useState(null);
  const [kitPreviewOpen, setKitPreviewOpen] = useState(false);
  const [loadingKitPreview, setLoadingKitPreview] = useState(false);
  const [canary, setCanary] = useState(null);

  // ---- form drafts (kept local so a step reset doesn't nuke inputs) ----
  const [affForm, setAffForm] = useState({
    name: "", contact_email: "", contact_phone: "",
  });
  const [inviteForm, setInviteForm] = useState({
    email: "", name: "", send_email: true, skip: false,
  });
  const [campaignName, setCampaignName] = useState("");
  // Iter-73.9 — inline "Create your first buyer" mini-form on step 3
  // so an admin with an empty tenant doesn't have to close the wizard
  // to seed a client.
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientForm, setNewClientForm] = useState({
    name: "", contact_email: "", contact_phone: "",
  });
  const [creatingClient, setCreatingClient] = useState(false);
  // Iter-73.10 — tracks whether the picked client was created via the
  // inline mini-form on step 3, so step 6 can offer a "Set intake
  // criteria now →" deep-link to `/clients/{id}/manage`. Brand-new
  // clients haven't had their states / case-types / eligibility rules
  // configured yet.
  const [newlyCreatedClientId, setNewlyCreatedClientId] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Load client list once so step 4 has a picker.
    api.get("/organizations?type=client&page_size=200")
      .then((r) => {
        const list = r.data?.organizations || [];
        setClients(list);
        // Iter-73.9 — auto-open the inline "create your first buyer" form
        // when the tenant has no clients yet.
        if (list.length === 0) setShowNewClientForm(true);
      })
      .catch(() => setClients([]));
  }, [open]);

  useEffect(() => {
    // Reset the wizard whenever it re-opens fresh.
    if (open && step === 1 && !affiliate) {
      setInviteResult(null); setCampaign(null); setKitSent(null); setCanary(null);
    }
  }, [open, step, affiliate]);

  if (!open) return null;

  // ─── Step actions ─────────────────────────────────────────
  const submitAffiliate = async (e) => {
    e.preventDefault();
    if (!affForm.name.trim() || !affForm.contact_email.trim()) {
      return toast.error("Name + contact email required");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/organizations", {
        type: "affiliate",
        name: affForm.name.trim(),
        contact_email: affForm.contact_email.trim(),
        contact_phone: affForm.contact_phone.trim() || null,
      });
      // Mint sandbox key immediately so the canary step later works.
      await api.post(`/organizations/${data.id}/mint-sandbox-key`);
      setAffiliate(data);
      setCampaignName(`${data.name} — Live Routing`);
      toast.success(`Affiliate "${data.name}" created + sandbox key minted`);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const submitInvite = async () => {
    if (inviteForm.skip) { setStep(3); return; }
    if (!inviteForm.email.includes("@")) {
      return toast.error("Valid email required (or check Skip)");
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/organizations/${affiliate.id}/invite-vendor`,
        { email: inviteForm.email.trim(),
          name: inviteForm.name.trim(),
          send_email: inviteForm.send_email },
      );
      setInviteResult(data);
      toast.success(
        data.status === "linked_existing"
          ? `Linked existing user ${data.email}`
          : `Invite sent to ${data.email}`,
      );
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };

  const submitClient = () => {
    if (!pickedClientId) return toast.error("Pick a client");
    setStep(4);
  };

  // Iter-73.9 — inline "Create your first buyer" (only visible when
  // the tenant has no clients OR the admin clicks "+ Create new"). Uses
  // the same `POST /organizations` endpoint, then auto-selects the row.
  const submitNewClient = async (e) => {
    e.preventDefault();
    if (!newClientForm.name.trim() || !newClientForm.contact_email.trim()) {
      return toast.error("Name + contact email required");
    }
    setCreatingClient(true);
    try {
      const { data } = await api.post("/organizations", {
        type: "client",
        name: newClientForm.name.trim(),
        contact_email: newClientForm.contact_email.trim(),
        contact_phone: newClientForm.contact_phone.trim() || null,
      });
      setClients((prev) => [data, ...prev]);
      setPickedClientId(data.id);
      setNewlyCreatedClientId(data.id);
      setShowNewClientForm(false);
      toast.success(
        `Client "${data.name}" created — you can set states/case-types at /clients/${data.id}/manage after the wizard.`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Client create failed");
    } finally { setCreatingClient(false); }
  };

  const submitCampaign = async () => {
    if (!campaignName.trim()) return toast.error("Campaign name required");
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns", {
        name: campaignName.trim(),
        client_id: pickedClientId,
        // Iter-74 correction — bind the wizard-minted campaign to this
        // vendor so a lead posted with Vendor B's api_key can never
        // reach Buyer A via this campaign.
        affiliate_id: affiliate?.id || null,
      });
      setCampaign(data);
      toast.success(`Campaign "${data.name}" created`);
      setStep(5);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Campaign create failed");
    } finally { setBusy(false); }
  };

  const submitSendKit = async (send) => {
    if (!send) { setStep(6); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/organizations/${affiliate.id}/send-integration-kit`,
        {
          to_email: affiliate.contact_email,
          // Iter-74 — include the wizard-minted campaign id so the
          // emailed curl example threads it in. Vendor doesn't need
          // to hunt for the campaign_id in the UI.
          campaign_id: campaign?.id || null,
        },
      );
      setKitSent(data);
      toast.success(`Kit sent to ${data.to_email}`);
      setStep(6);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  // Iter-73.9 — inline kit preview so the admin can copy the kit into
  // Gmail / Outlook without ever leaving the wizard. Iter-74 threads
  // the wizard-minted campaign_id into the preview.
  const openKitPreview = async () => {
    setLoadingKitPreview(true);
    try {
      const params = campaign?.id ? `?campaign_id=${encodeURIComponent(campaign.id)}` : "";
      const { data } = await api.get(
        `/organizations/${affiliate.id}/integration-kit${params}`,
      );
      setKitPreview(data);
      setKitPreviewOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load kit preview");
    } finally { setLoadingKitPreview(false); }
  };

  const fireCanary = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/organizations/${affiliate.id}/test-canary-lead`,
        { state: "AL", accident_type: "auto",
          campaign_id: campaign?.id || null },
      );
      setCanary(data);
      toast.success(`Canary fired — lead ${data.lead_id?.slice(0, 8)}…`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Canary failed");
    } finally { setBusy(false); }
  };

  const finish = () => {
    onFinished?.(affiliate);
    onClose?.();
  };

  const StepBadge = ({ n, active, done, label }) => (
    <div className={`flex items-center gap-2 flex-1 min-w-[120px] ${
      active ? "opacity-100" : done ? "opacity-70" : "opacity-40"
    }`}>
      {done
        ? <CheckCircle size={16} weight="fill" className="text-emerald-600" />
        : <Circle size={16} weight={active ? "fill" : "regular"} />}
      <span className={`text-[10px] uppercase tracking-widest font-bold ${
        active ? "text-black" : "text-zinc-600"
      }`}>{n}. {label}</span>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4"
      data-testid="vendor-wizard-modal"
    >
      <div className="bg-white border-2 border-black shadow-[8px_8px_0_#000] w-full max-w-3xl my-8">
        <div className="flex items-center justify-between border-b-2 border-black p-4">
          <div className="flex items-center gap-2">
            <Rocket size={18} weight="fill" />
            <div>
              <div className="font-chivo font-black uppercase tracking-tight text-lg">
                Affiliate Onboarding Wizard
              </div>
              <div className="label-mono">create → invite → route → verify · 90s</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="wizard-close"
            className="border border-black p-2 hover:bg-black hover:text-white"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Step tracker */}
        <div className="border-b-2 border-black p-3 flex flex-wrap gap-3 bg-zinc-50">
          <StepBadge n={1} active={step === 1} done={step > 1} label="Affiliate" />
          <StepBadge n={2} active={step === 2} done={step > 2} label="Invite user" />
          <StepBadge n={3} active={step === 3} done={step > 3} label="Pick buyer" />
          <StepBadge n={4} active={step === 4} done={step > 4} label="Campaign" />
          <StepBadge n={5} active={step === 5} done={step > 5} label="Kit" />
          <StepBadge n={6} active={step === 6} done={false} label="Canary" />
        </div>

        <div className="p-5 space-y-4">
          {/* ─── Step 1 · Affiliate ─── */}
          {step === 1 && (
            <form onSubmit={submitAffiliate} className="space-y-3" data-testid="wizard-step-1">
              <div className="flex items-center gap-2 mb-2">
                <Buildings size={14} weight="bold" />
                <div className="font-chivo font-black uppercase tracking-tight">
                  Create the affiliate org
                </div>
              </div>
              <div className="text-xs text-zinc-600 leading-relaxed">
                The <b>affiliate</b> is the company sending you traffic — think "Acme Traffic".
                Live + sandbox API keys mint automatically on save.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Company name *" value={affForm.name}
                       onChange={(v) => setAffForm({ ...affForm, name: v })}
                       testId="wizard-aff-name" placeholder="Acme Traffic LLC" />
                <Field label="Contact email *" value={affForm.contact_email}
                       onChange={(v) => setAffForm({ ...affForm, contact_email: v })}
                       testId="wizard-aff-email" type="email"
                       placeholder="ops@acme-traffic.com" />
                <Field label="Contact phone" value={affForm.contact_phone}
                       onChange={(v) => setAffForm({ ...affForm, contact_phone: v })}
                       testId="wizard-aff-phone" placeholder="555 555 0100" />
              </div>
              <div className="flex justify-end">
                <button type="submit" disabled={busy}
                        data-testid="wizard-aff-submit"
                        className="bg-black text-white border border-black px-4 py-2 uppercase text-[11px] tracking-widest font-bold disabled:opacity-50 inline-flex items-center gap-2">
                  {busy ? "Creating…" : "Create affiliate"}
                  <ArrowRight size={11} weight="bold" />
                </button>
              </div>
            </form>
          )}

          {/* ─── Step 2 · Invite user ─── */}
          {step === 2 && (
            <div className="space-y-3" data-testid="wizard-step-2">
              <div className="flex items-center gap-2 mb-2">
                <UserPlus size={14} weight="bold" />
                <div className="font-chivo font-black uppercase tracking-tight">
                  Invite the affiliate's login user
                </div>
              </div>
              <div className="text-xs text-zinc-600 leading-relaxed">
                Optional. Gives one human at <b>{affiliate?.name}</b> read-only
                access to <span className="font-mono">/vendor</span> so they can
                watch their own submit / accept / reject counts.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Email" value={inviteForm.email}
                       onChange={(v) => setInviteForm({ ...inviteForm, email: v })}
                       testId="wizard-invite-email" type="email"
                       placeholder="kristina@acme-traffic.com" />
                <Field label="Name" value={inviteForm.name}
                       onChange={(v) => setInviteForm({ ...inviteForm, name: v })}
                       testId="wizard-invite-name" placeholder="Kristina Herd" />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={inviteForm.skip}
                         onChange={(e) => setInviteForm({ ...inviteForm, skip: e.target.checked })}
                         data-testid="wizard-invite-skip"
                         className="w-4 h-4" />
                  <span className="text-xs">Skip — I'll invite them later</span>
                </label>
                <button type="button" onClick={submitInvite} disabled={busy}
                        data-testid="wizard-invite-submit"
                        className="bg-black text-white border border-black px-4 py-2 uppercase text-[11px] tracking-widest font-bold disabled:opacity-50 inline-flex items-center gap-2">
                  {busy ? "Working…" : inviteForm.skip ? "Skip" : "Send invite"}
                  <ArrowRight size={11} weight="bold" />
                </button>
              </div>
            </div>
          )}

          {/* ─── Step 3 · Pick buyer ─── */}
          {step === 3 && (
            <div className="space-y-3" data-testid="wizard-step-3">
              <div className="flex items-center gap-2 mb-2">
                <Buildings size={14} weight="bold" />
                <div className="font-chivo font-black uppercase tracking-tight">
                  Route this affiliate's traffic to a buyer
                </div>
              </div>

              {/* Empty-state / new-client path (Iter-73.9). */}
              {clients.length === 0 && !showNewClientForm && (
                <div className="border-2 border-amber-700 bg-amber-50 p-3 text-xs">
                  <b>No buyers yet.</b> Create your first one below — you can
                  configure states, case types, and pricing after the wizard
                  at <span className="font-mono">/clients/&hellip;/manage</span>.
                </div>
              )}
              {showNewClientForm ? (
                <form onSubmit={submitNewClient}
                      className="border-2 border-black bg-emerald-50 p-3 space-y-2"
                      data-testid="wizard-new-client-form">
                  <div className="flex items-center gap-2">
                    <Plus size={12} weight="bold" />
                    <div className="font-chivo font-black uppercase tracking-tight text-sm">
                      Create your first buyer
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Field label="Company name *" value={newClientForm.name}
                           onChange={(v) => setNewClientForm({ ...newClientForm, name: v })}
                           testId="wizard-new-client-name"
                           placeholder="Smith Law Firm" />
                    <Field label="Contact email *" value={newClientForm.contact_email}
                           onChange={(v) => setNewClientForm({ ...newClientForm, contact_email: v })}
                           testId="wizard-new-client-email" type="email"
                           placeholder="intake@smithlaw.com" />
                    <Field label="Contact phone" value={newClientForm.contact_phone}
                           onChange={(v) => setNewClientForm({ ...newClientForm, contact_phone: v })}
                           testId="wizard-new-client-phone" placeholder="555 555 0101" />
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    {clients.length > 0 && (
                      <button type="button" onClick={() => setShowNewClientForm(false)}
                              data-testid="wizard-new-client-cancel"
                              className="border border-black px-3 py-1.5 uppercase text-[10px] tracking-widest hover:bg-black hover:text-white">
                        Cancel
                      </button>
                    )}
                    <button type="submit" disabled={creatingClient}
                            data-testid="wizard-new-client-submit"
                            className="bg-emerald-700 text-white border border-emerald-800 px-3 py-1.5 uppercase text-[10px] tracking-widest font-bold disabled:opacity-50 inline-flex items-center gap-1">
                      {creatingClient ? "Creating…" : "Create client"}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <select value={pickedClientId}
                          onChange={(e) => setPickedClientId(e.target.value)}
                          data-testid="wizard-client-select"
                          className="w-full border border-black px-3 py-2 bg-white">
                    <option value="">— select a client —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.status && c.status !== "active" ? ` (${c.status})` : ""}
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <button type="button" onClick={() => setShowNewClientForm(true)}
                            data-testid="wizard-open-new-client"
                            className="border-2 border-black bg-white px-3 py-1.5 uppercase text-[10px] tracking-widest font-bold hover:bg-black hover:text-white inline-flex items-center gap-1">
                      <Plus size={10} weight="bold" /> Create new
                    </button>
                    <button type="button" onClick={submitClient} disabled={!pickedClientId}
                            data-testid="wizard-client-submit"
                            className="bg-black text-white border border-black px-4 py-2 uppercase text-[11px] tracking-widest font-bold disabled:opacity-50 inline-flex items-center gap-2">
                      Continue <ArrowRight size={11} weight="bold" />
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ─── Step 4 · Campaign ─── */}
          {step === 4 && (
            <div className="space-y-3" data-testid="wizard-step-4">
              <div className="flex items-center gap-2 mb-2">
                <Broadcast size={14} weight="bold" />
                <div className="font-chivo font-black uppercase tracking-tight">
                  Mint the routing campaign
                </div>
              </div>
              <div className="text-xs text-zinc-600 leading-relaxed">
                Campaigns are the billing container. The affiliate's webhook posts
                include <span className="font-mono">campaign_id</span>, which
                resolves to the client + destination.
              </div>
              <Field label="Campaign name *" value={campaignName}
                     onChange={setCampaignName} testId="wizard-campaign-name" />
              <div className="flex justify-end">
                <button type="button" onClick={submitCampaign} disabled={busy}
                        data-testid="wizard-campaign-submit"
                        className="bg-black text-white border border-black px-4 py-2 uppercase text-[11px] tracking-widest font-bold disabled:opacity-50 inline-flex items-center gap-2">
                  {busy ? "Creating…" : "Create campaign"}
                  <ArrowRight size={11} weight="bold" />
                </button>
              </div>
            </div>
          )}

          {/* ─── Step 5 · Kit ─── */}
          {step === 5 && (
            <div className="space-y-3" data-testid="wizard-step-5">
              <div className="flex items-center gap-2 mb-2">
                <PaperPlaneTilt size={14} weight="bold" />
                <div className="font-chivo font-black uppercase tracking-tight">
                  Send the integration kit
                </div>
              </div>
              <div className="text-xs text-zinc-600 leading-relaxed">
                Two ways to deliver the kit — one email fires from LeadFlow via
                Resend, the other opens a copy-paste preview so you can send it
                from your personal address.
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <button type="button" onClick={openKitPreview} disabled={loadingKitPreview}
                        data-testid="wizard-kit-copy-personal"
                        className="border-2 border-black bg-white px-3 py-2 uppercase text-[10px] tracking-widest font-bold hover:bg-black hover:text-white inline-flex items-center gap-2 disabled:opacity-50"
                        title="Preview the kit and copy the body for pasting into Gmail / Outlook">
                  <Copy size={11} weight="bold" />
                  {loadingKitPreview ? "Loading…" : "Copy for personal email"}
                </button>
                <button type="button" onClick={() => submitSendKit(false)}
                        data-testid="wizard-kit-skip"
                        className="border border-black px-3 py-2 uppercase text-[10px] tracking-widest hover:bg-black hover:text-white">
                  Skip
                </button>
                <button type="button" onClick={() => submitSendKit(true)} disabled={busy}
                        data-testid="wizard-kit-send"
                        className="bg-black text-white border border-black px-4 py-2 uppercase text-[11px] tracking-widest font-bold disabled:opacity-50 inline-flex items-center gap-2">
                  {busy ? "Sending…" : "Send from LeadFlow"}
                  <ArrowRight size={11} weight="bold" />
                </button>
              </div>
            </div>
          )}

          {/* ─── Step 6 · Canary + summary ─── */}
          {step === 6 && (
            <div className="space-y-4" data-testid="wizard-step-6">
              <div className="flex items-center gap-2 mb-2">
                <Flask size={14} weight="bold" />
                <div className="font-chivo font-black uppercase tracking-tight">
                  Verify with a canary lead
                </div>
              </div>
              <div className="text-xs text-zinc-600 leading-relaxed">
                Posts a synthetic sandbox lead using the affiliate's sandbox
                key. Exercises the whole prod pipeline — you'll see it in
                <span className="font-mono"> /leads</span> and (if criteria
                match) at the buyer.
              </div>
              {!canary ? (
                <div className="flex justify-end">
                  <button type="button" onClick={fireCanary} disabled={busy}
                          data-testid="wizard-canary-fire"
                          className="bg-black text-white border border-black px-4 py-2 uppercase text-[11px] tracking-widest font-bold disabled:opacity-50 inline-flex items-center gap-2">
                    <Flask size={11} weight="bold" />
                    {busy ? "Firing…" : "Fire canary"}
                  </button>
                </div>
              ) : (
                <div className={`border-2 p-3 text-xs ${
                  canary.webhook_status === 200
                    ? "border-emerald-700 bg-emerald-50"
                    : "border-amber-700 bg-amber-50"
                }`} data-testid="wizard-canary-result">
                  Webhook returned <b>{canary.webhook_status}</b>. Lead{" "}
                  <code className="font-mono">{canary.lead_id?.slice(0, 8)}…</code>
                  {" "}created — check <a href={canary.lead_url} target="_blank"
                       rel="noreferrer" className="underline font-bold">/leads</a>.
                </div>
              )}

              {/* Recap */}
              <div className="border-2 border-black p-3 bg-zinc-50 text-xs space-y-1">
                <div className="font-chivo font-black uppercase tracking-tight mb-1">
                  Summary
                </div>
                <div>✓ Affiliate: <b>{affiliate?.name}</b> · <span className="font-mono">{affiliate?.id?.slice(0, 8)}…</span></div>
                <div>
                  {inviteResult
                    ? `✓ Invited: ${inviteResult.email}`
                    : "· User invite skipped"}
                </div>
                <div>✓ Buyer: <b>{clients.find((c) => c.id === pickedClientId)?.name || pickedClientId}</b>
                  {newlyCreatedClientId === pickedClientId && (
                    <span className="ml-1 text-amber-800 font-bold">· needs criteria</span>
                  )}
                </div>
                <div>✓ Campaign: <b>{campaign?.name}</b> · <span className="font-mono">{campaign?.public_id}</span></div>
                <div>{kitSent ? `✓ Kit emailed to ${kitSent.to_email}` : "· Kit send skipped"}</div>
                <div>{canary ? `✓ Canary lead ${canary.lead_id?.slice(0, 8)}…` : "· Canary pending"}</div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2">
                {/* Iter-73.10 — for brand-new clients created via the
                    inline form on step 3, offer a deep-link straight
                    to /clients/{id}/manage so ops sets intake_criteria
                    before the vendor's real leads flow in. */}
                {newlyCreatedClientId && (
                  <a
                    href={`/clients/${newlyCreatedClientId}/manage`}
                    data-testid="wizard-set-criteria"
                    className="border-2 border-sky-700 bg-sky-50 text-sky-900 px-3 py-2 uppercase text-[10px] tracking-widest font-bold hover:bg-sky-700 hover:text-white inline-flex items-center gap-1"
                    title="Configure accepted states, case types, incident age, and other intake criteria for this buyer"
                  >
                    Set intake criteria now <ArrowSquareOut size={11} weight="bold" />
                  </a>
                )}
                <a href={`/affiliates`} data-testid="wizard-open-aff"
                   className="border border-black bg-white px-3 py-2 uppercase text-[10px] tracking-widest font-bold hover:bg-black hover:text-white inline-flex items-center gap-1">
                  <ArrowSquareOut size={11} weight="bold" /> Open affiliates
                </a>
                <button type="button" onClick={finish}
                        data-testid="wizard-finish"
                        className="bg-emerald-700 text-white border border-emerald-800 px-4 py-2 uppercase text-[11px] tracking-widest font-bold inline-flex items-center gap-2">
                  <CheckCircle size={11} weight="fill" /> Finish
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Iter-73.9 — inline kit preview overlay. Rendered here so it stacks
          on top of the wizard modal without navigating away. */}
      {kitPreviewOpen && kitPreview && (
        <IntegrationKitPreview
          kit={kitPreview}
          onClose={() => setKitPreviewOpen(false)}
        />
      )}
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text", placeholder }) {
  return (
    <div>
      <label className="label-mono">{label}</label>
      <input type={type} value={value}
             onChange={(e) => onChange(e.target.value)}
             data-testid={testId}
             placeholder={placeholder}
             className="w-full border border-black px-3 py-2 mt-1" />
    </div>
  );
}